import { CommonModule } from '@angular/common';
import { Component, OnInit, effect, inject, signal, untracked } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ToastService } from 'angular-toastify';
import { ParkingPaymentLog, ParkingPaymentLogDto } from '../../../../core/models/system-settings/parking-master-data.model';
import { MapperParkingMasterDataService } from '../../../../core/mappers/system-settings/mapper-parking-master-data.service';
import { ApiParkingMasterDataService } from '../../../../core/services/api/system-settings/api-parking-master-data.service';
import { SiteStateService } from '../../../../core/services/site-state.service';
import { PssLoadingComponent } from '../../../../tools/template/pss/pss-loading/pss-loading.component';

@Component({
  selector: 'app-parking-payment-log',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    PssLoadingComponent,
  ],
  templateUrl: './parking-payment-log.component.html',
  styleUrl: './parking-payment-log.component.css',
})
export class ParkingPaymentLogComponent implements OnInit {
  readonly siteState = inject(SiteStateService);
  private readonly api = inject(ApiParkingMasterDataService);
  private readonly mapper = inject(MapperParkingMasterDataService);
  private readonly toast = inject(ToastService);

  items = signal<ParkingPaymentLog[]>([]);
  isLoading = signal(false);
  isDetailLoading = signal(false);
  isModalOpen = signal(false);
  selectedId = signal(0);
  selectedDetail = signal<ParkingPaymentLog | null>(null);
  searchText = signal('');
  startDate = signal(this.formatDateInput(new Date()));
  endDate = signal(this.formatDateInput(new Date()));
  lastFetchedAt = signal<Date | null>(null);
  currentPage = signal(1);
  pageSize = signal(20);
  pageSizeOptions: number[] = [10, 20, 50, 100];
  totalItems = signal(0);
  totalPages = signal(1);

  constructor() {
    effect(() => {
      const site = this.siteState.selectedSite();
      if (!site?.id) return;
      untracked(() => {
        this.currentPage.set(1);
        this.loadTable();
      });
    });
  }

  get selectedSiteId(): number { return this.siteState.selectedSite()?.id ?? 0; }

  get startItemIndex(): number {
    if (this.totalItems() === 0) return 0;
    return (this.currentPage() - 1) * this.pageSize() + 1;
  }

  get endItemIndex(): number {
    return Math.min(this.currentPage() * this.pageSize(), this.totalItems());
  }

  get pagesArray(): number[] {
    const maxVisibleButtons = 5;
    let startPage = Math.max(1, this.currentPage() - 2);
    let endPage = Math.min(this.totalPages(), startPage + maxVisibleButtons - 1);
    if (endPage - startPage < maxVisibleButtons - 1) {
      startPage = Math.max(1, endPage - maxVisibleButtons + 1);
    }
    return Array.from({ length: endPage - startPage + 1 }, (_, i) => startPage + i);
  }

  ngOnInit(): void { if (this.selectedSiteId) this.loadTable(); }

  onSearchChange(): void { this.currentPage.set(1); this.loadTable(); }
  onDateChange(): void {
    if (!this.isDateRangeValid()) return;
    this.currentPage.set(1);
    this.loadTable();
  }
  onRefresh(): void { this.currentPage.set(1); this.loadTable(); }

  openDetail(item: ParkingPaymentLog): void {
    this.selectedId.set(item.id);
    this.selectedDetail.set(null);
    this.isModalOpen.set(true);
    this.isDetailLoading.set(true);
    this.api.getParkingPaymentLogById(item.id).subscribe({
      next: (res) => {
        this.selectedDetail.set(this.mapper.mapPaymentLog(res as ParkingPaymentLogDto));
        this.isDetailLoading.set(false);
      },
      error: () => {
        this.isDetailLoading.set(false);
        this.toast.error('โหลดรายละเอียดล้มเหลว');
      },
    });
  }

  closeModal(): void {
    this.isModalOpen.set(false);
    this.selectedId.set(0);
    this.selectedDetail.set(null);
  }

  setPage(page: number): void {
    if (page >= 1 && page <= this.totalPages()) { this.currentPage.set(page); this.loadTable(); }
  }

  onPageSizeChange(event: Event): void {
    const element = event.target as HTMLSelectElement;
    this.pageSize.set(Number(element.value));
    this.currentPage.set(1);
    this.loadTable();
  }

  trackById(_: number, item: ParkingPaymentLog): number { return item.id; }

  loadTable(): void {
    const siteId = this.selectedSiteId;
    if (!siteId) { this.items.set([]); return; }
    if (!this.isDateRangeValid()) return;

    this.isLoading.set(true);
    this.api.getParkingPaymentLogForTable({
      page: this.currentPage(),
      pageSize: this.pageSize(),
      searchText: this.searchText().trim() || undefined,
      siteId,
      startDate: this.startDate(),
      endDate: this.endDate(),
    }).subscribe({
      next: (res) => {
        const mapped = this.mapper.mapTable<ParkingPaymentLogDto, ParkingPaymentLog>(
          res,
          (item) => this.mapper.mapPaymentLog(item)
        );
        this.items.set(mapped.data);
        this.totalItems.set(mapped.total);
        this.totalPages.set(Math.max(1, Math.ceil(mapped.total / this.pageSize())));
        this.lastFetchedAt.set(new Date());
        this.isLoading.set(false);
      },
      error: () => { this.items.set([]); this.isLoading.set(false); this.toast.error('โหลดข้อมูลล้มเหลว'); },
    });
  }

  private isDateRangeValid(): boolean {
    if (!this.startDate() || !this.endDate()) {
      this.toast.error('กรุณาระบุวันที่เริ่มต้นและวันที่สิ้นสุด');
      return false;
    }

    if (this.startDate() > this.endDate()) {
      this.toast.error('วันที่เริ่มต้นต้องไม่เกินวันที่สิ้นสุด');
      return false;
    }

    return true;
  }

  private formatDateInput(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }
}
